import { useWindowSize } from "hooks/useWindowSize";
import dayjs from "dayjs";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Search } from "components/DesignSystem/Search/Search";
import { Button } from "components/DesignSystem/Button/Button";
import { DateFilter } from "components/Filters/DateFilter";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { ArrowRight } from "components/icons/ArrowRight";
import { DropDownItem } from "components/GeneralLedger/GeneralLedgerFilters";
import {
  ACCOUNTING_METHOD_TYPES,
  ACCOUNTING_METHODS,
  SUMMARY_REPORT,
  SUMMARY_REPORT_TYPES,
  monthPeriod,
} from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import { PERIOD, PeriodType } from "constants/revenueMetrics";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { ComponentProps, useState } from "react";
import { useLazyGetAllMerchantsQuery } from "store/apis/transactions";
import { TxnCategories } from "types/Models/books";
import { debounce } from "utils/debouncing";
import { flattenTypes } from "utils/flattenCOA";
import { formatDateByViewType } from "utils/formatDate";
import { getDatePeriod } from "utils/getDatePeriod";
import { DateRangeValue, getDateRangeWithSetting } from "utils/getDateRange";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Loader from "components/DesignSystem/Loader/loader";

export type FilterValues = {
  START_DATE: string;
  END_DATE: string;
  ACCOUNTING_METHOD: ACCOUNTING_METHOD_TYPES;
  VENDORS: string[];
  CATEGORY: string[];
  VIEW_BY: PeriodType;
  SELECT_PERIOD: string;
  COMPARISON_SUMMARY_TYPE: SUMMARY_REPORT_TYPES;
  RELATED_PARTY: boolean;
};

const dateFilterMap: {
  [P in PeriodType]: ComponentProps<typeof DateFilter>["pickerType"];
} = {
  TOTALS: "date",
  MONTHLY: "month",
  QUARTERLY: "quarter",
  YEARLY: "year",
};

const PERIOD_LABELS: Record<string, string> = {
  [PERIOD.TOTALS]: "None",
  [PERIOD.MONTHLY]: "Month",
  [PERIOD.QUARTERLY]: "Quarter",
  [PERIOD.YEARLY]: "Year",
};

const GROUP_BY_LABELS: Record<string, string> = {
  [SUMMARY_REPORT.VENDOR]: "Vendor",
  [SUMMARY_REPORT.CATEGORY]: "Category",
};

export const ViewByDropdown = ({
  filterValues,
  updateFilter,
}: {
  filterValues: FilterValues;
  updateFilter: <K extends keyof FilterValues>(
    key: K,
    value: FilterValues[K]
  ) => void;
}) => {
  const groupByLabel = GROUP_BY_LABELS[filterValues.COMPARISON_SUMMARY_TYPE];

  const title = groupByLabel || null;

  return (
    <Dropdown.Root modal={false}>
      <Dropdown.Trigger className="all:unset t-group t-self-start">
        <Button size="small">
          <span className="t-text-text-30">View by:</span>
          {title && <span className="t-ml-1 t-text-text-60">{title}</span>}
          <div className="t-text-text-30 t-transition-transform t-duration-200 t-rotate-90 group-data-[state=open]:-t-rotate-90">
            <ArrowRight color="currentColor" size="22" stroke="1.25" />
          </div>
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          sideOffset={8}
          className="!t-min-w-40 t-max-h-96 t-overflow-y-auto t-p-2.5"
          align="start"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="t-flex t-flex-col t-gap-1">
            <div className="t-flex t-flex-col t-gap-2">
              <Dropdown.Label className="t-group t-w-full t-truncate !t-border-none !t-p-1">
                Period
              </Dropdown.Label>
              <RadioGroup.Root
                value={filterValues.VIEW_BY}
                onValueChange={(value: PeriodType) =>
                  updateFilter("VIEW_BY", value)
                }
              >
                <RadioGroup.Content className="t-gap-2 t-flex t-flex-col">
                  {Object.entries(PERIOD_LABELS).map(([value, label]) => (
                    <div key={value} className="t-p-1">
                      <RadioGroup.Item value={value}>{label}</RadioGroup.Item>
                    </div>
                  ))}
                </RadioGroup.Content>
              </RadioGroup.Root>
              <Dropdown.Separator />
            </div>
            <div className="t-flex t-flex-col t-gap-2">
              <Dropdown.Label className="t-group t-w-full t-truncate !t-border-none !t-p-1">
                Group by
              </Dropdown.Label>
              <RadioGroup.Root
                value={filterValues.COMPARISON_SUMMARY_TYPE}
                onValueChange={(value: SUMMARY_REPORT_TYPES) =>
                  updateFilter("COMPARISON_SUMMARY_TYPE", value)
                }
              >
                <RadioGroup.Content className="t-gap-2 t-flex t-flex-col">
                  {Object.entries(GROUP_BY_LABELS).map(([value, label]) => (
                    <div key={value} className="t-p-1">
                      <RadioGroup.Item value={value}>{label}</RadioGroup.Item>
                    </div>
                  ))}
                </RadioGroup.Content>
              </RadioGroup.Root>
            </div>
          </div>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

const DateFilterBody = ({
  filterValues,
  updateFilter,
}: {
  filterValues: FilterValues;
  updateFilter: <K extends keyof FilterValues>(
    key: K,
    value: FilterValues[K]
  ) => void;
}) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const datePeriod = getDatePeriod;
  const type = dateFilterMap[filterValues.VIEW_BY] || "date";

  const values = {
    SELECT_PERIOD: filterValues.SELECT_PERIOD,
    START_DATE: filterValues.START_DATE
      ? dayjs(filterValues.START_DATE).toString()
      : "",
    END_DATE: filterValues.END_DATE
      ? dayjs(filterValues.END_DATE).toString()
      : "",
  };

  const onDateChange: ComponentProps<typeof BooksDateFilter>["updateFilter"] = (
    name,
    value
  ) => {
    if (!value) return;

    if (name === "SELECT_PERIOD" && value !== "CUSTOM") {
      const { startDate, endDate } = getDateRangeWithSetting(
        value as DateRangeValue,
        bookkeepingSettings?.tax_year_end,
        bookkeepingStartDate
      );
      updateFilter("START_DATE", startDate.format(YYYY_MM_DD));
      updateFilter("END_DATE", endDate.format(YYYY_MM_DD));
    }
    updateFilter(name as keyof FilterValues, value);
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full">
      {type === "date" && (
        <BooksDateFilter
          datePeriod={datePeriod}
          skipDateUpdateOnPeriodChange
          pickerType="date"
          values={values}
          updateFilter={onDateChange}
        />
      )}
      {type === "month" && (
        <BooksDateFilter
          skipDateUpdateOnPeriodChange
          pickerType="month"
          values={values}
          updateFilter={onDateChange}
          datePeriod={monthPeriod}
        />
      )}
      {type === "quarter" && (
        <BooksDateFilter
          skipDateUpdateOnPeriodChange
          pickerType="quarter"
          values={values}
          updateFilter={onDateChange}
          showPeriodSelector={false}
        />
      )}
      {type === "year" && (
        <BooksDateFilter
          skipDateUpdateOnPeriodChange
          pickerType="year"
          values={values}
          updateFilter={onDateChange}
          showPeriodSelector={false}
        />
      )}
    </div>
  );
};

const CategoryFilterBody = ({
  filterValues,
  updateFilter,
}: {
  filterValues: FilterValues;
  updateFilter: <K extends keyof FilterValues>(
    key: K,
    value: FilterValues[K]
  ) => void;
}) => {
  const [search, setSearch] = useState("");

  const { chartOfAccounts, isLoading, isSuccess } = useChartOfAccounts({
    search,
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });

  const categoryIds = filterValues.CATEGORY;

  const onHandleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    types?: TxnCategories[]
  ) => {
    const { value, checked } = e.target;
    const selectedChilds =
      flattenTypes({ accounts: types! })?.map(({ uuid }) => uuid) || [];

    let newCategories: Set<string> = new Set();
    if (checked) {
      newCategories = new Set([...categoryIds, ...selectedChilds, value]);
    } else {
      newCategories = new Set(
        categoryIds.filter(
          (categoryId) =>
            !selectedChilds.includes(categoryId) && categoryId !== value
        )
      );
    }

    updateFilter("CATEGORY", newCategories.size > 0 ? [...newCategories] : []);
  };

  const handleSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value.toLowerCase() || "");
  });

  const noCOAFound = chartOfAccounts.length === 0;

  return (
    <div className="t-h-full t-overflow-auto">
      <div className="t-sticky t-top-0 t-pb-2 t-z-10 t-bg-white">
        <Search
          placeholder="Search..."
          onChange={handleSearch}
          autoFocus
          customSize="regular"
          block
        />
      </div>

      <Async.Root
        isEmpty={noCOAFound}
        isLoading={isLoading}
        isSuccess={isSuccess}
        customLoader={
          <div className="t-flex t-max-h-28 t-h-28 t-items-center t-justify-center">
            <Loader />
          </div>
        }
      >
        <Async.Empty>
          <div className="t-flex t-max-h-28 t-h-28 t-items-center t-justify-center">
            No categories found
          </div>
        </Async.Empty>
        <Async.Success>
          <DropDownItem
            types={chartOfAccounts}
            level={0}
            onHandleChange={onHandleChange}
            categoryIds={categoryIds}
          />
        </Async.Success>
      </Async.Root>
    </div>
  );
};

const OthersFilter = ({
  filterValues,
  updateFilter,
}: {
  filterValues: FilterValues;
  updateFilter: <K extends keyof FilterValues>(
    key: K,
    value: FilterValues[K]
  ) => void;
}) => (
  <div className="t-flex t-flex-col t-gap-3">
    <Checkbox
      label={<>Related party</>}
      name="related_party"
      checked={Boolean(filterValues.RELATED_PARTY)}
      onChange={(e) => updateFilter("RELATED_PARTY", e.target.checked)}
    />
  </div>
);

export const VendorFilter = ({
  filterValues,
  updateFilter,
}: {
  filterValues: FilterValues;
  updateFilter: <K extends keyof FilterValues>(
    key: K,
    value: FilterValues[K]
  ) => void;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [searchTerm, setSearchTerm] = useState("");
  const { isMobile } = useWindowSize();

  const appliedFilterCount = [
    filterValues.START_DATE || filterValues.END_DATE,
    true,
    filterValues.VENDORS.length > 0,
    filterValues.CATEGORY.length > 0,
    filterValues.RELATED_PARTY,
  ].filter(Boolean).length;

  const { data: merchants, loadNext } = usePaginatedQuery<{
    merchants?: string[];
  }>(
    useLazyGetAllMerchantsQuery,
    "merchants",
    {
      search_term: searchTerm,
      groupId,
      entityId,
    },
    ["groupId", "entityId"]
  );

  return (
    <Filter.Root
      defaultValue="VIEW_BY"
      hideDivider={isMobile}
      title={
        isMobile ? (
          <span className="t-text-text-30">({appliedFilterCount})</span>
        ) : undefined
      }
      capsule={
        <span className="t-contents sm:t-hidden">
          {(filterValues.START_DATE || filterValues.END_DATE) && (
            <Chip
              onRemove={() => {}}
              isRemovable={false}
              isActive
              filterType="VIEW_BY"
            >
              {formatDateByViewType(
                filterValues.START_DATE,
                filterValues.VIEW_BY
              )}
              {" to "}
              {formatDateByViewType(
                filterValues.END_DATE,
                filterValues.VIEW_BY
              )}
            </Chip>
          )}
          <Chip
            onRemove={() => {}}
            isRemovable={false}
            isActive
            filterType="ACCOUNTING_METHOD"
          >
            <div className="first-letter:t-uppercase t-lowercase">
              {filterValues.ACCOUNTING_METHOD} method
            </div>
          </Chip>

          {Boolean(filterValues.VENDORS.length) && (
            <Chip
              onRemove={() => updateFilter("VENDORS", [])}
              isActive
              filterType="VENDORS"
            >
              Vendors ({filterValues.VENDORS.length})
            </Chip>
          )}
          {Boolean(filterValues.CATEGORY.length) && (
            <Chip
              onRemove={() => updateFilter("CATEGORY", [])}
              isActive
              filterType="CATEGORY"
            >
              Category ({filterValues.CATEGORY.length})
            </Chip>
          )}
          <Chip
            onClick={() =>
              updateFilter("RELATED_PARTY", !filterValues.RELATED_PARTY)
            }
            onRemove={() => updateFilter("RELATED_PARTY", false)}
            isRemovable
            isFixedFilter
            isActive={filterValues.RELATED_PARTY}
            filterType="OTHERS"
          >
            Related party
          </Chip>
        </span>
      }
    >
      <Filter.Portal>
        <Filter.List>
          <Filter.ListItem value="VIEW_BY">Date</Filter.ListItem>
          <Filter.ListItem value="ACCOUNTING_METHOD">
            Accounting Method
          </Filter.ListItem>
          <Filter.ListItem value="VENDORS">Vendors</Filter.ListItem>
          <Filter.ListItem value="CATEGORY">Category</Filter.ListItem>
          <Filter.ListItem value="OTHERS">Others</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="VIEW_BY" block>
          <DateFilterBody
            filterValues={filterValues}
            updateFilter={updateFilter}
          />
        </Filter.Body>
        <Filter.Body value="ACCOUNTING_METHOD" block>
          <RadioGroup.Root
            className="t-mb-6"
            onValueChange={(value: typeof filterValues.ACCOUNTING_METHOD) => {
              updateFilter("ACCOUNTING_METHOD", value);
            }}
            value={filterValues.ACCOUNTING_METHOD}
          >
            <RadioGroup.Content>
              <RadioGroup.Item value={ACCOUNTING_METHODS.CASH}>
                Cash method
              </RadioGroup.Item>
              <RadioGroup.Item value={ACCOUNTING_METHODS.ACCRUAL}>
                Accrual method
              </RadioGroup.Item>
            </RadioGroup.Content>
          </RadioGroup.Root>
        </Filter.Body>
        <Filter.Body value="VENDORS" block>
          {merchants?.merchants && (
            <MultiSelectFilter
              loadNext={loadNext}
              hasMore={merchants.current_page !== merchants.total_pages}
              totalCount={merchants.merchants.length}
              options={
                merchants.merchants.map((merchant) => ({
                  label: merchant,
                  value: merchant,
                })) || []
              }
              onChange={(value: string[]) => updateFilter("VENDORS", value)}
              onSearchValueChange={debounce(setSearchTerm)}
              selected={filterValues.VENDORS}
            />
          )}
        </Filter.Body>
        <Filter.Body value="CATEGORY" block>
          <CategoryFilterBody
            filterValues={filterValues}
            updateFilter={updateFilter}
          />
        </Filter.Body>
        <Filter.Body value="OTHERS" block>
          <OthersFilter
            filterValues={filterValues}
            updateFilter={updateFilter}
          />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
